import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import { physicalAIIntelligencePages } from "../data/physicalAIIntelligence";
import { jobsIntelligencePages } from "../data/physicalAIJobs";
import { seoConfig } from "../data/seoConfig";

const physicalAIDataPages = physicalAIIntelligencePages.filter(
  (page) => page.path.startsWith("/data/") && !page.existing,
);
const workforceDataPages = jobsIntelligencePages.filter((page) => page.path.startsWith("/data/"));

const humanoidDataPages = [
  {
    path: "/humanoid-intelligence",
    title: "Humanoid Robotics Intelligence Database",
    description: "Connected evidence for humanoid models, deployments, failures, production, shipments and funding.",
  },
  {
    path: "/humanoid-robots/database",
    title: "Humanoid Robot Database",
    description: "Search verified humanoid robot specifications, manufacturers, prices and commercial status.",
  },
  {
    path: "/humanoid-robots/compare",
    title: "Humanoid Robot Comparison",
    description: "Compare source-backed humanoid specifications without filling unknown fields with estimates.",
  },
  {
    path: "/robotics/humanoid-robot-data",
    title: "Humanoid Robot Data and Charts",
    description: "Source-linked charts covering robot prices, hardware, availability, deployment and reliability evidence.",
  },
  {
    path: "/china-humanoid-model-tracker",
    title: "China Humanoid Robot Model Tracker",
    description: "Track verified Chinese humanoid models, manufacturers, public prices and commercial status.",
  },
  {
    path: "/china-humanoid-shipment-tracker",
    title: "China Humanoid Robot Shipment Tracker",
    description: "Separate source-backed production milestones, shipments and deliveries instead of merging them.",
  },
  {
    path: "/humanoid-robot-deployment-map",
    title: "Humanoid Robot Deployment Database",
    description: "Document operating sites, customers, tasks, deployment status and unit counts when disclosed.",
  },
  {
    path: "/humanoid-robot-failures",
    title: "Humanoid Robot Failure Database",
    description: "Review documented failures, intervention notes, observation type and denominators when available.",
  },
];

const groups = [
  {
    eyebrow: "Physical AI evidence",
    title: "Physical AI databases",
    description: "Benchmarks, real-robot tests, training data, latency, deployments and documented failure evidence.",
    pages: physicalAIDataPages.map((page) => ({
      path: page.path,
      title: page.title,
      description: page.description,
      records: page.records?.length || 0,
    })),
  },
  {
    eyebrow: "Robotics labor market",
    title: "Jobs and talent data",
    description: "Hiring activity, skills, disclosed salaries, public talent moves and university-to-industry links.",
    pages: workforceDataPages.map((page) => ({
      path: page.path,
      title: page.title,
      description: page.description,
      records: page.records?.length || 0,
    })),
  },
  {
    eyebrow: "Humanoid intelligence",
    title: "Humanoid robot datasets",
    description: "Robot catalogs, comparisons, deployments, failures, shipments and industry charts.",
    pages: humanoidDataPages,
  },
];

const allPages = groups.flatMap((group) => group.pages);
const datedRecords = [...physicalAIDataPages, ...workforceDataPages];
const structuredRecordCount = datedRecords.reduce((total, page) => total + (page.records?.length || 0), 0);

const breadcrumbs = [
  { label: "Home", to: "/" },
  { label: "Data", to: "/data" },
];

const collectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Robotics and Physical AI Data Hub",
  description:
    "TechniaHQRobot data hub for Physical AI benchmarks, real-robot tests, training data, inference latency, deployments, failures, hiring, salaries and humanoid robotics intelligence.",
  url: `${seoConfig.baseUrl}/data`,
  dateModified: "2026-08-17",
  isPartOf: { "@type": "WebSite", name: "TechniaHQRobot", url: seoConfig.baseUrl },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: allPages.length,
    itemListElement: allPages.map((page, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: page.title,
      url: `${seoConfig.baseUrl}${page.path}`,
    })),
  },
};

function DataCard({ page }) {
  return (
    <article className="border border-white/10 bg-[#07101f]/90 p-5 transition hover:border-cyan-300/30 hover:bg-[#0a1528]">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-lg font-black leading-7 tracking-tight text-white">{page.title}</h3>
        {typeof page.records === "number" && (
          <span className="shrink-0 border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
            {page.records} records
          </span>
        )}
      </div>
      <p className="mt-3 text-sm leading-7 text-slate-400">{page.description}</p>
      <Link to={page.path} className="mt-5 inline-flex text-sm font-black text-cyan-300 hover:text-cyan-100">
        Open database →
      </Link>
    </article>
  );
}

export default function DataHub() {
  return (
    <>
      <SEO
        title="Robotics & Physical AI Data Hub and Benchmarks TechniaHQRobot"
        description="Browse TechniaHQRobot databases for Physical AI benchmarks, real-robot evidence, training data, latency, deployments, failures, robotics hiring and humanoid robot intelligence."
        path="/data"
        modifiedTime="2026-08-17"
        schema={[buildBreadcrumbSchema(breadcrumbs), collectionSchema]}
      />
      <div className="bg-[#030712] text-slate-100">
        <div className="border-b border-white/10">
          <Breadcrumbs items={breadcrumbs} />
        </div>
        <div className="relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            aria-hidden="true"
            style={{
              backgroundImage:
                "linear-gradient(rgba(56,189,248,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.05) 1px,transparent 1px)",
              backgroundSize: "38px 38px",
            }}
          />
          <div className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-3xl" aria-hidden="true" />

          <section className="site-shell relative py-10 sm:py-14">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">TECHNIAHQROBOT DATA</p>
            <h1 className="mt-4 max-w-5xl text-4xl font-black tracking-[-0.04em] text-white sm:text-6xl">
              Robotics and Physical AI Data Hub
            </h1>
            <p className="mt-5 max-w-4xl text-base leading-8 text-slate-300 sm:text-lg">
              Explore robotics data by topic. Compare published benchmarks, robot specifications, deployment evidence and hiring records, with links to the underlying sources.
            </p>
            <div className="mt-7 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
              <div className="bg-[#07101f] px-5 py-5">
                <div className="text-3xl font-black text-white">{allPages.length}</div>
                <div className="mt-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">linked data views</div>
              </div>
              <div className="bg-[#07101f] px-5 py-5">
                <div className="text-3xl font-black text-white">{structuredRecordCount}</div>
                <div className="mt-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">structured records in /data views</div>
              </div>
              <div className="bg-[#07101f] px-5 py-5">
                <div className="text-3xl font-black text-white">3</div>
                <div className="mt-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">connected research clusters</div>
              </div>
            </div>
          </section>

          <div className="site-shell relative pb-20">
            <section className="border border-white/10 bg-white/[0.025] p-5 sm:p-6">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-300">Data integrity</p>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-white">Evidence stays attached to its conditions</h2>
              <p className="mt-4 max-w-5xl text-sm leading-7 text-slate-400">
                Benchmark scores are kept with robot, environment and protocol. Public hiring counts are not treated as workforce size. Orders, production, shipments and deployments remain separate. Unknown values stay unknown instead of being estimated.
              </p>
            </section>

            {groups.map((group) => (
              <section key={group.title} className="mt-10" aria-labelledby={`data-${group.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="max-w-4xl">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">{group.eyebrow}</p>
                  <h2 id={`data-${group.title.replace(/\s+/g, "-").toLowerCase()}`} className="mt-3 text-3xl font-black tracking-tight text-white">
                    {group.title}
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-400">{group.description}</p>
                </div>
                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {group.pages.map((page) => <DataCard key={page.path} page={page} />)}
                </div>
              </section>
            ))}

            <section className="mt-10 grid gap-4 border-t border-white/10 pt-8 md:grid-cols-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Source policy</p>
                <p className="mt-2 text-sm leading-7 text-slate-400">Retained records preserve source URLs and distinguish reported facts from calculations or editorial interpretation.</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Using the databases</p>
                <p className="mt-2 text-sm leading-7 text-slate-400">Choose a topic below, then use its filters to find records. Check the source and verification date before comparing results from different robots or environments.</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.15em] text-cyan-300">Related research</p>
                <p className="mt-2 text-sm leading-7 text-slate-400">
                  Continue with <Link to="/physical-ai" className="font-black text-cyan-300 hover:text-cyan-100">Physical AI</Link>,{" "}
                  <Link to="/jobs" className="font-black text-cyan-300 hover:text-cyan-100">Jobs Intelligence</Link> or{" "}
                  <Link to="/humanoid-intelligence" className="font-black text-cyan-300 hover:text-cyan-100">Humanoid Intelligence</Link>.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
